/**
 * VM control plane client (PR #5).
 *
 * Mirrors the BE surface in crates/z01-control-plane/src/handlers/vms.rs.
 */
import { tokenStorage } from "@/auth/storage";
import { apiFetch, throwApiRequestError } from "./client";
import type { CreateVmRequest, VmRecord } from "./generated";

export function listVms(): Promise<VmRecord[]> {
  return apiFetch<VmRecord[]>("/api/v1/vms");
}

export function getVm(id: string): Promise<VmRecord> {
  return apiFetch<VmRecord>(`/api/v1/vms/${encodeURIComponent(id)}`);
}

export function createVm(req: CreateVmRequest): Promise<VmRecord> {
  return apiFetch<VmRecord>("/api/v1/vms", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
}

export function startVm(id: string): Promise<VmRecord> {
  return apiFetch<VmRecord>(`/api/v1/vms/${encodeURIComponent(id)}/start`, {
    method: "POST",
  });
}

export function stopVm(id: string): Promise<VmRecord> {
  return apiFetch<VmRecord>(`/api/v1/vms/${encodeURIComponent(id)}/stop`, {
    method: "POST",
  });
}

/**
 * DELETE returns 204 No Content; bypass `apiFetch` (which expects JSON).
 */
export async function deleteVm(id: string): Promise<void> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const tokens = tokenStorage.load();
  if (tokens?.access_token) {
    headers.Authorization = `Bearer ${tokens.access_token}`;
  }
  const res = await fetch(`/api/v1/vms/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers,
  });
  if (!res.ok) {
    await throwApiRequestError(res);
  }
}

/**
 * Build the WS URL for the VM terminal endpoint, threading the bearer
 * token through a query parameter (browsers can't set Authorization on
 * a WebSocket handshake). The BE accepts either header or query.
 *
 * On production at `www.z01.ai`, the Vercel rewrite proxies `/api/*`
 * over HTTP but cannot tunnel a WebSocket upgrade, so the terminal
 * has to connect directly to the API origin. `VITE_API_WS_ORIGIN` is
 * the override (set in Vercel production env to `wss://api.z01.ai`).
 * In dev (or any local hostname) we keep the same-origin path so the
 * Vite proxy / k3s ingress can serve it.
 *
 * Returned URL is absolute (`ws://` or `wss://`).
 */
export function vmTerminalUrl(id: string): string {
  const tokens = tokenStorage.load();
  const override = import.meta.env.VITE_API_WS_ORIGIN;
  const path = `/api/v1/vms/${encodeURIComponent(id)}/terminal`;
  const qs = tokens?.access_token
    ? `?token=${encodeURIComponent(tokens.access_token)}`
    : "";
  if (override) {
    // Strip any trailing slash, accept ws:// or wss:// or http(s)://.
    const trimmed = override.replace(/\/$/, "");
    const swapped = trimmed.replace(/^http(s?):\/\//, "ws$1://");
    return `${swapped}${path}${qs}`;
  }
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}${path}${qs}`;
}

export function vmEventsUrl(id: string): string {
  return `/api/v1/vms/${encodeURIComponent(id)}/events`;
}
